import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { FarmForm, OrchardMap } from '../../components/orchard';
import { Maximize2, Loader2 } from 'lucide-react';
import orchardService from '../../services/orchard';
import type { Farm, GeoJSONPolygon } from '../../types/orchard';
import { toNum } from '@/utils/numeric';

function FarmEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [farm, setFarm] = useState<Farm | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [polygon, setPolygon] = useState<GeoJSONPolygon | null>(null);
  const [areaFromMap, setAreaFromMap] = useState<number | null>(null);

  const farmId = Number(id);

  const loadFarm = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await orchardService.getFarm(farmId);
      if (response.success && response.data) {
        setFarm(response.data);
        setPolygon(response.data.boundary_geometry);
        setAreaFromMap(toNum(response.data.total_area_hectares));
      } else {
        setError('Failed to load farm');
      }
    } catch {
      setError('Failed to load farm. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [farmId]);

  useEffect(() => {
    loadFarm();
  }, [loadFarm]);

  const handlePolygonCreated = useCallback(async (newPolygon: GeoJSONPolygon) => {
    setPolygon(newPolygon);
    try {
      const response = await orchardService.calculateArea(newPolygon);
      if (response.success && response.data) {
        setAreaFromMap(response.data.hectares);
      }
    } catch {
      setAreaFromMap(null);
    }
  }, []);

  const handlePolygonEdited = useCallback(async (editedPolygon: GeoJSONPolygon) => {
    setPolygon(editedPolygon);
    try {
      const response = await orchardService.calculateArea(editedPolygon);
      if (response.success && response.data) {
        setAreaFromMap(response.data.hectares);
      }
    } catch {
      setAreaFromMap(null);
    }
  }, []);

  // Needed while the boundary is being edited, not only at save time — the
  // region suggestion follows whatever is currently drawn.
  const centerPoint = useMemo(
    () => (polygon ? orchardService.calculateCenterPoint(polygon.coordinates) : null),
    [polygon]
  );

  const handleSubmit = async (fieldData: {
    name: string;
    description?: string;
    location?: string;
    total_area_hectares?: number;
    growing_region_id?: number | null;
    code?: string;
    active?: boolean;
  }) => {
    if (!polygon) return;

    setIsSubmitting(true);
    setError(null);

    const centerPoint = orchardService.calculateCenterPoint(polygon.coordinates);

    try {
      const response = await orchardService.updateFarm(farmId, {
        name: fieldData.name,
        description: fieldData.description,
        location: fieldData.location,
        boundary_geometry: polygon,
        center_point: centerPoint,
        total_area_hectares: fieldData.total_area_hectares ?? areaFromMap ?? undefined,
        growing_region_id: fieldData.growing_region_id ?? null,
        code: fieldData.code || undefined,
        active: fieldData.active,
      });

      if (response.success) {
        navigate(`/orchard/farms/${farmId}`);
      } else {
        setError((response as any).message || 'Failed to update farm');
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Edit Farm"
          subtitle="Loading..."
          icon={<Maximize2 className="h-6 w-6 text-white" />}
          showBackButton
        />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (error && !farm) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Edit Farm"
          subtitle="Error"
          icon={<Maximize2 className="h-6 w-6 text-white" />}
          showBackButton
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-error-50 border border-error-200 rounded-xl p-6 text-center">
            <p className="text-error-700 mb-4">{error}</p>
            <Button variant="outline" onClick={loadFarm}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={`Edit: ${farm?.name || 'Farm'}`}
        subtitle="Update farm details and boundary"
        icon={<Maximize2 className="h-6 w-6 text-white" />}
        showBackButton
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-error-50 border border-error-200 rounded-lg p-4">
            <p className="text-error-700">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-base font-semibold text-neutral-900 mb-4">Farm Details</h2>
              {farm && (
                <FarmForm
                  initialValues={{
                    name: farm.name,
                    description: farm.description ?? undefined,
                    location: farm.location ?? undefined,
                    total_area_hectares: toNum(farm.total_area_hectares) ?? undefined,
                    growing_region_id: farm.growing_region_id,
                    code: farm.code ?? undefined,
                    active: farm.active,
                  }}
                  areaFromMap={areaFromMap}
                  hasPolygon={!!polygon}
                  centerPoint={centerPoint}
                  onSubmit={handleSubmit}
                  onCancel={() => navigate(`/orchard/farms/${farmId}`)}
                  isSubmitting={isSubmitting}
                />
              )}
            </CardContent>
          </Card>

          {/* Map */}
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
              <p className="text-sm text-blue-800 font-medium">
                The existing boundary is shown. You can draw a new polygon to replace it, or edit the existing one.
              </p>
            </div>
            <div className="rounded-xl overflow-hidden border border-neutral-200 shadow-sm" style={{ height: '560px' }}>
              <OrchardMap
                farmBoundary={polygon ?? undefined}
                drawMode="farm"
                onPolygonCreated={handlePolygonCreated}
                onPolygonEdited={handlePolygonEdited}
                center={
                  farm?.center_point
                    ? [farm.center_point.lat, farm.center_point.lng]
                    : undefined
                }
                zoom={15}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FarmEdit;
